"use client";

import { QueryClient } from "@tanstack/react-query";

/**
 * The app's single `QueryClient`.
 *
 * **`retry` is the setting that matters, and getting it wrong is not subtle.** `platformFetch`
 * already does its own refresh-and-retry on a 401, and coalesces so a burst of simultaneous
 * expiries produces one refresh. Query's default retry (three attempts with backoff) would run
 * *on top of* that: one expired access cookie would become four refreshes, and the token broker
 * answers 503 on lease contention, so most of them would fail and surface as an error anyway.
 *
 * So: never retry a 4xx. Those are answers, not failures -- a 401 has already been through the
 * refresh path and a 404 will not become a 200. A 5xx or a network error is worth one retry,
 * which is what a genuinely transient upstream blip looks like.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;
  const status = (error as { status?: number } | null)?.status;
  if (typeof status === "number" && status >= 400 && status < 500) return false;
  return true;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // Long enough that navigating between admin pages reads from cache instead of
        // refetching the same reference lists -- `model-aliases`, `mcp-servers` and
        // `knowledge-bases` are each fetched by three different consoles today, and every
        // navigation refetches all of them. Short enough that an admin edit made in another
        // tab shows up without a reload.
        staleTime: 30_000,
        // The default refetches whenever the tab regains focus, which for an admin table is
        // mostly a surprise: rows shift under the cursor after an alt-tab. Mutations invalidate
        // explicitly, which is the signal that actually means something changed.
        refetchOnWindowFocus: false,
      },
      mutations: {
        // A mutation is not idempotent by default. Retrying a failed `POST /v1/sessions` could
        // create two sessions, and there is no way for this layer to know which routes are safe.
        retry: false,
      },
    },
  });
}
